import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useAuth } from '../contexts/AuthContext';

// Componente de credencial miniatura flotante con movimiento SMOOTH - OPTIMIZADO
const FloatingCredencial = ({ card, onPositionUpdate }) => {
  const [isVisible, setIsVisible] = useState(false);
  const positionRef = useRef({ x: card.left, y: card.top });
  const elementRef = useRef(null);
  const animationRef = useRef(null);
  const [, forceUpdate] = useState(0);

  // Velocidad estable SIN regeneración constante
  const velocityRef = useRef({
    x: (Math.random() - 0.5) * 1.2,
    y: (Math.random() - 0.5) * 1.2,
  });

  // Ciclo de visibilidad
  useEffect(() => {
    const timeout = setTimeout(() => setIsVisible(true), card.delay * 1000);
    const visibilityInterval = setInterval(() => {
      setIsVisible((prev) => !prev);
    }, card.duration * 1000);

    return () => {
      clearTimeout(timeout);
      clearInterval(visibilityInterval);
    };
  }, [card.delay, card.duration]);

  // Movimiento OPTIMIZADO - frame limitado y transform en GPU
  useEffect(() => {
    if (!isVisible) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    let lastTimestamp = 0;
    const FRAME_INTERVAL = 1000 / 30; // 30 FPS máximo para reducir carga

    const animate = (timestamp) => {
      if (!lastTimestamp) {
        lastTimestamp = timestamp;
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const delta = Math.min(0.033, (timestamp - lastTimestamp) / 1000);

      if (delta >= FRAME_INTERVAL / 1000) {
        // Actualizar posición
        let newX = positionRef.current.x + velocityRef.current.x * delta * 60;
        let newY = positionRef.current.y + velocityRef.current.y * delta * 60;

        // Rebote en bordes
        if (newX <= 2) {
          newX = 2;
          velocityRef.current.x = Math.abs(velocityRef.current.x);
        } else if (newX >= 98) {
          newX = 98;
          velocityRef.current.x = -Math.abs(velocityRef.current.x);
        }

        if (newY <= 2) {
          newY = 2;
          velocityRef.current.y = Math.abs(velocityRef.current.y);
        } else if (newY >= 98) {
          newY = 98;
          velocityRef.current.y = -Math.abs(velocityRef.current.y);
        }

        positionRef.current = { x: newX, y: newY };

        // Actualizar DOM directamente sin setState
        if (elementRef.current) {
          elementRef.current.style.left = `${newX}%`;
          elementRef.current.style.top = `${newY}%`;
        }

        // Notificar posición (solo cada ciertos frames)
        if (onPositionUpdate && Math.random() < 0.3) {
          onPositionUpdate(card.id, { left: newX, top: newY });
        }

        forceUpdate((prev) => prev + 1);
        lastTimestamp = timestamp;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isVisible, card.id, onPositionUpdate]);

  const Diamond = ({ size = 4, color, animate = false }) => (
    <div
      className={`${animate ? 'animate-pulse-slow' : ''}`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        transform: 'rotate(45deg)',
        backgroundColor: color,
        boxShadow: `0 0 4px ${color}`,
        willChange: 'transform, opacity',
      }}
    />
  );

  return (
    <div
      ref={elementRef}
      className="fixed transition-opacity duration-1000 ease-in-out"
      style={{
        left: `${card.left}%`,
        top: `${card.top}%`,
        transform: 'translate(-50%, -50%) translateZ(0)',
        opacity: isVisible ? 1 : 0,
        zIndex: 1,
        pointerEvents: 'none',
        willChange: 'left, top, opacity, transform',
      }}
    >
      <div
        className="relative w-32 h-44 rounded-xl overflow-hidden shadow-xl"
        style={{
          background: `linear-gradient(135deg, ${card.color.normal} 0%, ${card.color.fuerte} 100%)`,
          transform: 'translateZ(0)',
          willChange: 'transform',
        }}
      >
        {/* Fondo decorativo */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, ${card.color.claro} 1px, transparent 1px)`,
              backgroundSize: '10px 10px',
            }}
          />
        </div>

        {/* ROMBOS DECORATIVOS - Versión simplificada */}
        <div className="absolute top-2 left-2 flex gap-0.5">
          <Diamond size={5} color={card.color.claro} animate />
          <Diamond size={7} color="white" />
          <Diamond size={4} color={card.color.claro} animate />
        </div>

        <div className="absolute top-2 right-2 flex gap-0.5">
          <Diamond size={4} color={card.color.claro} animate />
          <Diamond size={7} color="white" />
          <Diamond size={5} color={card.color.claro} animate />
        </div>

        <div className="absolute bottom-2 left-2 flex gap-0.5">
          <Diamond size={5} color={card.color.claro} animate />
          <Diamond size={6} color="white" />
          <Diamond size={4} color={card.color.claro} animate />
        </div>

        <div className="absolute bottom-2 right-2 flex gap-0.5">
          <Diamond size={4} color={card.color.claro} animate />
          <Diamond size={6} color="white" />
          <Diamond size={5} color={card.color.claro} animate />
        </div>

        {/* ROMBO CENTRAL */}
        <div
          className="absolute"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%) rotate(45deg)',
            width: '80px',
            height: '80px',
            border: `3px solid ${card.color.claro}`,
            opacity: 0.15,
          }}
        />

        {/* Logo pequeño */}
        <div className="absolute left-1/2 transform -translate-x-1/2 top-0 w-16 h-5 bg-white/90 rounded-b-md flex items-center justify-center">
          <img
            src="/media/PACKSYS.png"
            alt="Packsys"
            className="h-3 object-contain"
            loading="lazy"
          />
        </div>

        {/* Contenido */}
        <div className="relative p-3 flex flex-col items-center text-center h-full">
          <div className="mt-6 mb-2">
            <div className="w-14 h-14 rounded-lg border-2 border-white/80 bg-white/20 mx-auto flex items-center justify-center">
              <svg
                className="w-6 h-6 text-white/60"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
          </div>

          <div className="space-y-1 w-full">
            <div className="h-2 bg-white/30 rounded-full w-16 mx-auto"></div>
            <div className="h-2 bg-white/30 rounded-full w-20 mx-auto"></div>
          </div>

          <div className="mt-2">
            <div className="h-1.5 bg-white/40 rounded-full w-12 mx-auto"></div>
          </div>

          <div className="mt-2">
            <div className="h-1.5 bg-white/30 rounded-full w-20 mx-auto"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Función para verificar colisiones (sin cambios)
const isPositionValid = (
  newLeft,
  newTop,
  existingPositions,
  minDistance = 12
) => {
  for (const pos of existingPositions) {
    const dx = Math.abs(pos.left - newLeft);
    const dy = Math.abs(pos.top - newTop);
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance < minDistance) {
      return false;
    }
  }
  return true;
};

// Función para generar posición aleatoria (sin cambios)
const getRandomPosition = (existingPositions, maxAttempts = 50) => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const left = 5 + Math.random() * 90;
    const top = 5 + Math.random() * 90;

    if (isPositionValid(left, top, existingPositions, 12)) {
      return { left, top };
    }
  }
  return { left: 5 + Math.random() * 90, top: 5 + Math.random() * 90 };
};

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, error } = useAuth();
  const [floatingCards, setFloatingCards] = useState([]);
  const cardPositionsRef = useRef(new Map());
  const animationFrameRef = useRef(null);

  // Colores disponibles (sin cambios)
  const colorOptions = useMemo(
    () => [
      {
        normal: '#F68C2A',
        fuerte: '#E05A1A',
        claro: '#FBB036',
        nombre: 'NARANJA',
      },
      {
        normal: '#064382',
        fuerte: '#002140',
        claro: '#0065A4',
        nombre: 'AZUL',
      },
      {
        normal: '#59B2AA',
        fuerte: '#02B4A6',
        claro: '#1E9FAB',
        nombre: 'VERDE',
      },
      {
        normal: '#4297C8',
        fuerte: '#284090',
        claro: '#64C2E0',
        nombre: 'CELESTE',
      },
      {
        normal: '#766CAF',
        fuerte: '#3F3F92',
        claro: '#BBABD3',
        nombre: 'MORADO',
      },
    ],
    []
  );

  // Actualizar posiciones (optimizado)
  const handlePositionUpdate = useCallback((cardId, newPosition) => {
    cardPositionsRef.current.set(cardId, newPosition);
  }, []);

  // Crear tarjetas flotantes (sin cambios)
  const createFloatingCards = useCallback(() => {
    const isMobile = window.innerWidth < 768;
    const cardCount = isMobile ? 8 : 24;
    const cards = [];
    const positions = [];

    for (let i = 0; i < cardCount; i++) {
      const color =
        colorOptions[Math.floor(Math.random() * colorOptions.length)];
      const { left, top } = getRandomPosition(positions, 30);
      positions.push({ left, top });

      cards.push({
        id: i,
        color: color,
        left: left,
        top: top,
        delay: Math.random() * 2,
        duration: 4 + Math.random() * 3,
      });
    }
    return cards;
  }, [colorOptions]);

  // Inicializar tarjetas (sin cambios)
  useEffect(() => {
    setFloatingCards(createFloatingCards());
  }, [createFloatingCards]);

  // Regenerar posiciones periódicamente (reducida frecuencia)
  useEffect(() => {
    const repositionInterval = setInterval(() => {
      setFloatingCards((prevCards) => {
        const currentPositions = Array.from(cardPositionsRef.current.values());
        return prevCards.map((card) => {
          if (Math.random() < 0.1) {
            // Reducido de 0.2 a 0.1
            const { left, top } = getRandomPosition(currentPositions, 20);
            return { ...card, left, top };
          }
          return card;
        });
      });
    }, 10000); // Aumentado de 6000 a 10000 ms

    return () => clearInterval(repositionInterval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const result = await login(username, password);
    setIsLoading(false);

    if (!result.success && result.error) {
      console.error(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Degradado de fondo animado - OPTIMIZADO */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
          }}
        />

        {/* Blobs de fondo con animaciones suavizadas */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500 rounded-full blur-3xl opacity-20 animate-pulse-slower"></div>
        <div
          className="absolute top-0 right-0 w-80 h-80 bg-purple-500 rounded-full blur-3xl opacity-20 animate-pulse-slower"
          style={{ animationDelay: '1s' }}
        ></div>
        <div
          className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-500 rounded-full blur-3xl opacity-20 animate-pulse-slower"
          style={{ animationDelay: '0.7s' }}
        ></div>
      </div>

      {/* Credenciales flotantes - OPTIMIZADO */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          maskImage:
            'radial-gradient(circle at center, transparent 30%, black 70%)',
          WebkitMaskImage:
            'radial-gradient(circle at center, transparent 30%, black 70%)',
          willChange: 'mask',
        }}
      >
        {floatingCards.map((card) => (
          <FloatingCredencial
            key={card.id}
            card={card}
            onPositionUpdate={handlePositionUpdate}
          />
        ))}
      </div>

      {/* Contenido del login - SIN CAMBIOS */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div
          className="text-center mb-8"
          style={{ animation: 'fadeInUp 0.6s ease-out forwards' }}
        >
          <div className="inline-flex items-center justify-center bg-white backdrop-blur-md rounded-2xl mb-4 shadow-2xl border border-white/20">
            <img
              src="/media/PACKSYS.png"
              alt="Packsys"
              className="h-24 p-2 object-contain"
              loading="eager"
            />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2 drop-shadow-lg">
            Credenciales Packsys
          </h1>
          <p className="text-blue-200 text-lg">
            Sistema de gestión de credenciales
          </p>
        </div>

        <div
          className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-white/20"
          style={{
            animation: 'fadeInUp 0.6s ease-out forwards',
            animationDelay: '0.2s',
            opacity: 0,
          }}
        >
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">
                Usuario
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300"
                placeholder="Ingresa tu usuario"
                required
                autoFocus
              />
            </div>

            <div className="mb-6">
              <label className="block text-white font-medium mb-2">
                Contraseña
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300"
                placeholder="Ingresa tu contraseña"
                required
              />
            </div>

            {error && (
              <div
                className="mb-4 p-3 bg-red-500/20 backdrop-blur-sm border border-red-400/50 rounded-xl"
                style={{ animation: 'shake 0.5s ease-in-out' }}
              >
                <p className="text-red-200 text-sm text-center">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Iniciando sesión...
                </span>
              ) : (
                'Iniciar Sesión'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/20">
            <p className="text-xs text-blue-200 text-center">
              Sistema exclusivo para administradores de Recursos Humanos
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.6;
            transform: rotate(45deg) scale(1);
          }
          50% {
            opacity: 1;
            transform: rotate(45deg) scale(1.1);
          }
        }

        @keyframes pulse-slower {
          0%, 100% {
            opacity: 0.2;
            transform: scale(1);
          }
          50% {
            opacity: 0.3;
            transform: scale(1.05);
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
          will-change: transform, opacity;
        }

        .animate-pulse-slower {
          animation: pulse-slower 4s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Login;
